/**
 * Platform errors, and the judgement that stops one re-alerting every morning.
 *
 * WHY IT SITS ON AN EXISTING ADMIN PAGE. The precedent for acknowledging
 * something on this platform is `acknowledgeFindingV2`: an admin-only callable
 * that writes an additive judgement, with the ChainDashboard RENDERING what was
 * judged. Measured before building — that callable is not wired to any button
 * today, so findings are judged out-of-band. This section keeps the precedent's
 * shape (same page, same admin gate, judged rows shown rather than hidden) and
 * adds the control, because an acknowledgment feature whose only interface is a
 * console call is not usable by the person it exists for.
 *
 * A JUDGED ERROR IS QUIET, NOT INVISIBLE — the same rule the chain view states
 * for findings. Hiding them would make an accidental permanent mute impossible
 * to notice, which is the failure this whole feature is trying to avoid.
 *
 * NO RAW ERROR TEXT REACHES THIS SCREEN. The summary is BUILT from named safe
 * fields by `errorRowSummary`, never rendered from the row, so a field that
 * appears in the store tomorrow cannot appear here. `errorAdminView.test.ts`
 * asserts that with a seeded message and stack.
 */
import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS } from '../../constants/firebase';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import {
  ERROR_ADJUDICATION_OPTIONS,
  errorRowSummary,
  sortErrorRows,
  type AdminErrorRow,
} from '../../features/errors/errorAdminView';

export default function PlatformErrorsSection() {
  const [rows, setRows] = useState<AdminErrorRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await httpsCallable(functions, CLOUD_FUNCTIONS.LIST_PLATFORM_ERRORS)({});
      setRows(sortErrorRows((r.data as { errors: AdminErrorRow[] }).errors ?? []));
      setError(null);
    } catch (e: unknown) {
      setError(String((e as { message?: string })?.message ?? e));
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const judge = useCallback(
    async (fingerprint: string, adjudication: string) => {
      setBusy(fingerprint);
      try {
        await httpsCallable(functions, CLOUD_FUNCTIONS.ACKNOWLEDGE_PLATFORM_ERROR)({
          fingerprint,
          adjudication,
        });
        await load();
      } catch (e: unknown) {
        setError(String((e as { message?: string })?.message ?? e));
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  return (
    <section className="border rounded bg-white p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        Platform errors (last 7 days)
      </h3>
      <p className="text-xs text-gray-600 mb-3">
        Errors captured from callables and from the browser. Judging one stops it appearing in
        the morning email — until it happens again, which always starts it over.
      </p>

      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}
      {!rows && !error && <p className="text-sm text-gray-500">Loading…</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-gray-500">Nothing captured in this window.</p>
      )}

      <div className="space-y-2">
        {(rows ?? []).map((row) => (
          <div
            key={row.fingerprint}
            className={`border rounded p-3 ${
              row.acknowledged ? 'bg-gray-50 border-gray-200' : 'bg-white border-amber-200'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm text-gray-900">{errorRowSummary(row)}</div>
              {row.acknowledged && (
                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
                  <CheckCircle2 className="w-3 h-3" />
                  Judged
                </span>
              )}
            </div>

            {row.acknowledgment && (
              <div className="mt-1 text-xs text-indigo-900">
                {row.acknowledgment.adjudication.replace(/-/g, ' ')} —{' '}
                {row.acknowledgment.acknowledgedBy} on{' '}
                {row.acknowledgment.acknowledgedAt.slice(0, 10)}
                {row.acknowledgment.note ? ` — ${row.acknowledgment.note}` : ''}
              </div>
            )}

            <div className="mt-2 flex flex-wrap gap-2">
              {ERROR_ADJUDICATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  title={opt.hint}
                  disabled={busy === row.fingerprint}
                  onClick={() => judge(row.fingerprint, opt.value)}
                  className="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
